import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Button, ButtonGroup, Spinner, Alert, Badge, Table,
} from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/light.css';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { InventoryAnalysisAPI } from '../../helpers/firebase_helper';

/* fmtDate: Date → 'YYYY-MM-DD' */
const fmtDate = (d) => {
    if (!d) return '';
    const dd = new Date(d);
    const y = dd.getFullYear();
    const m = String(dd.getMonth() + 1).padStart(2, '0');
    const day = String(dd.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/* ─────────────────────────────────────────────────────────────
   Constants
───────────────────────────────────────────────────────────── */
const QUARTERS = [
    { label: 'Current', value: 'current' },
    { label: 'Q1 (Apr–Jun)', value: 'Q1' },
    { label: 'Q2 (Jul–Sep)', value: 'Q2' },
    { label: 'Q3 (Oct–Dec)', value: 'Q3' },
    { label: 'Q4 (Jan–Mar)', value: 'Q4' },
];

// Build fiscal year list: current FY ± 2 years
// FY 2025 = FY2025-26 (Apr 2025 – Mar 2026)
const buildFiscalYears = () => {
    const now = new Date();
    // Indian FY starts in April; if month >= April, current FY start = this year
    const currentFYStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const years = [];
    for (let y = currentFYStart - 2; y <= currentFYStart + 1; y++) {
        years.push({ label: `FY${y}-${String(y + 1).slice(2)}`, value: y });
    }
    return years.reverse(); // newest first
};
const FISCAL_YEARS = buildFiscalYears();

const FORECAST_DAYS = [
    { label: '7D', value: 7 },
    { label: '15D', value: 15 },
    { label: '30D', value: 30 },
    { label: '60D', value: 60 },
    { label: '90D', value: 90 },
];

const RISK_CONFIG = {
    high: { color: '#f1416c', label: 'High Risk', icon: 'ri-error-warning-fill' },
    medium: { color: '#f6a723', label: 'Medium Risk', icon: 'ri-alert-fill' },
    low: { color: '#50cd89', label: 'Low Risk', icon: 'ri-checkbox-circle-fill' },
};

// Filter modes
const MODE_QUARTER = 'quarter';   // quarter + fiscal_year
const MODE_DAYS = 'days';      // forecast_days
const MODE_RANGE = 'range';     // start_date + end_date

const TENANT_ID = 1;

/* ─────────────────────────────────────────────────────────────
   Chart: Daily Order Counts & Quantity — dual-axis line
───────────────────────────────────────────────────────────── */
const buildDailyLineOptions = (dates) => ({
    chart: {
        id: 'daily-trend-chart',
        type: 'line',
        height: 340,
        toolbar: { show: false },
        fontFamily: 'Inter, sans-serif',
        background: 'transparent',
        zoom: { enabled: false },
        animations: { enabled: false },
    },
    colors: ['#6259ca', '#f5a623'],
    stroke: { show: true, curve: 'smooth', width: 3 },
    fill: { opacity: 1 },
    markers: { size: 0, hover: { sizeOffset: 5 } },
    xaxis: {
        type: 'category',
        categories: dates ?? [],
        tickAmount: Math.min((dates ?? []).length, 14),
        labels: {
            rotate: -40,
            style: { fontSize: '10px', colors: '#8794a6' },
            formatter: (val) => (val ? String(val).slice(5) : val),
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        crosshairs: { show: true },
    },
    yaxis: [
        {
            seriesName: 'Orders Count',
            axisTicks: { show: true, color: '#6259ca' },
            axisBorder: { show: true, color: '#6259ca' },
            labels: {
                style: { colors: ['#6259ca'] },
                formatter: (v) => (v != null ? Math.round(v) : 0),
            },
            title: {
                text: 'Orders / Day',
                style: { color: '#6259ca', fontSize: '11px', fontWeight: 600 },
            },
        },
        {
            seriesName: 'Qty Ordered',
            opposite: true,
            axisTicks: { show: true, color: '#f5a623' },
            axisBorder: { show: true, color: '#f5a623' },
            labels: {
                style: { colors: ['#f5a623'] },
                formatter: (v) => (v != null ? Math.round(v) : 0),
            },
            title: {
                text: 'Qty / Day',
                style: { color: '#f5a623', fontSize: '11px', fontWeight: 600 },
            },
        },
    ],
    legend: { show: true, position: 'top', horizontalAlign: 'center' },
    dataLabels: { enabled: false },
    grid: {
        borderColor: '#ececec',
        strokeDashArray: 4,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
    },
    tooltip: {
        shared: true,
        intersect: false,
        theme: 'light',
        y: [
            { formatter: (v) => (v != null ? `${Number(v).toFixed(0)} orders` : '0') },
            { formatter: (v) => (v != null ? `${Number(v).toFixed(0)} units` : '0') },
        ],
    },
});

/* ─────────────────────────────────────────────────────────────
   Chart: Top Products — horizontal bar (total_qty_ordered)
───────────────────────────────────────────────────────────── */
const buildTopProductsBarOptions = (categories) => ({
    chart: {
        type: 'bar',
        height: 360,
        toolbar: { show: false },
        fontFamily: 'Inter, sans-serif',
        background: 'transparent',
    },
    plotOptions: {
        bar: {
            horizontal: true,
            borderRadius: 4,
            barHeight: '58%',
            distributed: true,
        },
    },
    colors: [
        '#6259ca', '#f5a623', '#02c0f8', '#50cd89', '#f1416c',
        '#7986cb', '#4db6ac', '#ff7043', '#ab47bc', '#26a69a',
    ],
    xaxis: {
        categories,
        labels: { style: { fontSize: '11px', colors: '#8794a6' } },
    },
    yaxis: {
        labels: {
            style: { fontSize: '11px', colors: '#495057' },
            maxWidth: 210,
            formatter: (v) =>
                typeof v === 'string' && v.length > 30 ? v.slice(0, 28) + '…' : v,
        },
    },
    dataLabels: {
        enabled: true,
        style: { fontSize: '11px', fontWeight: 600 },
        formatter: (v) => `${v} units`,
        offsetX: 4,
    },
    legend: { show: false },
    grid: { borderColor: '#f1f1f1', xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    tooltip: {
        theme: 'light',
        y: { formatter: (v) => `${v} units ordered` },
    },
});

/* ─────────────────────────────────────────────────────────────
   Chart: Next-period predicted qty — horizontal bar
───────────────────────────────────────────────────────────── */
const buildPredictionBarOptions = (categories) => ({
    chart: {
        type: 'bar',
        height: 300,
        toolbar: { show: false },
        fontFamily: 'Inter, sans-serif',
        background: 'transparent',
    },
    plotOptions: {
        bar: { horizontal: true, borderRadius: 4, barHeight: '55%', distributed: true },
    },
    colors: ['#50cd89', '#f5a623', '#02c0f8', '#6259ca', '#f1416c', '#7986cb', '#4db6ac', '#ff7043', '#ab47bc', '#26a69a'],
    xaxis: {
        categories,
        labels: { style: { fontSize: '11px', colors: '#8794a6' } },
    },
    yaxis: {
        labels: {
            style: { fontSize: '11px', colors: '#495057' },
            maxWidth: 210,
            formatter: (v) =>
                typeof v === 'string' && v.length > 30 ? v.slice(0, 28) + '…' : v,
        },
    },
    dataLabels: {
        enabled: true,
        style: { fontSize: '11px', fontWeight: 600 },
        formatter: (v) => `${Number(v).toFixed(1)} units`,
        offsetX: 4,
    },
    legend: { show: false },
    grid: { borderColor: '#f1f1f1', xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    tooltip: {
        theme: 'light',
        y: { formatter: (v) => `${Number(v).toFixed(1)} units predicted` },
    },
});

/* ─────────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────────── */
const AnalysisPrediction = () => {
    // Active filter mode: 'quarter' | 'days' | 'range'
    const [filterMode, setFilterMode] = useState(MODE_QUARTER);

    // Quarter + Fiscal Year
    const [selectedQuarter, setSelectedQuarter] = useState('current');
    const [selectedFY, setSelectedFY] = useState(null);   // null = API uses current FY

    // Forecast Days (quick buttons)
    const [forecastDays, setForecastDays] = useState(null);

    // Custom Date Range — two YYYY-MM-DD strings
    const [startDate, setStartDate] = useState(fmtDate(new Date(Date.now() - 30 * 864e5)));
    const [endDate, setEndDate] = useState(fmtDate(new Date()));
    const [rangeApplied, setRangeApplied] = useState(false);
    const [rangeError, setRangeError] = useState('');


    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [apiMsg, setApiMsg] = useState('');

    document.title = 'Analysis & Prediction | Whistlemind';

    /* ── Build filters object for API call ── */
    const buildFilters = useCallback(() => {
        if (filterMode === MODE_RANGE) {
            return { startDate, endDate };
        } else if (filterMode === MODE_DAYS) {
            return { forecastDays };
        } else {
            return { quarter: selectedQuarter, fiscal_year: selectedFY };
        }
    }, [filterMode, selectedQuarter, selectedFY, forecastDays, startDate, endDate]);

    /* ── Fetch ── */
    const fetchAnalysis = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await InventoryAnalysisAPI.getInventoryAnalysis(
                buildFilters(), TENANT_ID
            );
            const data = response?.result ?? response;
            setResult(data);
            setApiMsg(response?.message || '');
        } catch (err) {
            console.error('[AnalysisPrediction] fetch error:', err);
            setError(err?.message || 'Failed to load analysis data. Please try again.');
            setResult(null);
        } finally {
            setLoading(false);
        }
    }, [buildFilters]);

    useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);

    /* ── Range apply handler ── */
    const applyRange = () => {
        if (!startDate || !endDate) { setRangeError('Please select a date range first.'); return; }
        setRangeError('');
        setRangeApplied(true);
        fetchAnalysis();
    };

    /* ── Derived: daily trend ── */
    const dailyDates = result?.overall_daily_dates ?? [];
    const dailyOrderCounts = result?.overall_daily_order_counts ?? [];
    const dailyQty = result?.overall_daily_qty ?? [];
    const dailyAmount = result?.overall_daily_amount ?? [];
    const hasData = result?.has_data === true && dailyDates.length > 0;

    const dailyLineOptions = buildDailyLineOptions(dailyDates);
    const dailyLineSeries = [
        { name: 'Orders Count', data: dailyOrderCounts },
        { name: 'Qty Ordered', data: dailyQty },
    ];

    // Aggregate KPIs
    const totalOrders = dailyOrderCounts.reduce((a, b) => a + (b ?? 0), 0);
    const totalQty = dailyQty.reduce((a, b) => a + (b ?? 0), 0);
    const totalRevenue = dailyAmount.reduce((a, b) => a + (b ?? 0), 0);
    const peakIdxOrders = dailyOrderCounts.length
        ? dailyOrderCounts.indexOf(Math.max(...dailyOrderCounts))
        : -1;
    const peakOrderDay = peakIdxOrders >= 0 ? dailyDates[peakIdxOrders] ?? '—' : '—';

    /* ── Derived: top products ── */
    const topProducts = result?.top_products ?? [];
    const topBarCats = topProducts.map(p => p.product_name);
    const topBarData = topProducts.map(p => p.total_qty_ordered ?? 0);
    const topBarOptions = buildTopProductsBarOptions(topBarCats);
    const topBarSeries = [{ name: 'Total Qty Ordered', data: topBarData }];

    /* ── Derived: next period prediction ── */
    const nextPeriod = result?.next_period ?? null;
    // predicted_qty is now an object: { low, mid, high, range }
    const nextProducts = (nextPeriod?.products ?? []).filter(p => (p.predicted_qty?.high ?? p.predicted_qty ?? 0) > 0);
    const predBarCats = nextProducts.map(p => p.product_name);
    const predBarData = nextProducts.map(p => p.predicted_qty?.high ?? p.predicted_qty ?? 0);
    const predBarOptions = buildPredictionBarOptions(predBarCats);
    const predBarSeries = [{ name: 'Predicted Qty (High)', data: predBarData }];

    // Reorder alert products
    const reorderNeeded = (nextPeriod?.products ?? []).filter(p => (p.recommended_reorder_qty ?? 0) > 0);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Analysis & Prediction" pageTitle="Dashboard" />

                    {/* ── Filter Bar ── */}
                    <Card className="mb-3 shadow-sm">
                        <CardBody className="py-3">

                            {/* Mode tabs */}
                            <div className="d-flex gap-2 mb-3 flex-wrap">
                                {[
                                    { mode: MODE_QUARTER, label: 'Quarter / FY', icon: 'ri-calendar-2-line' },
                                    { mode: MODE_DAYS, label: 'Forecast Days', icon: 'ri-time-line' },
                                    { mode: MODE_RANGE, label: 'Custom Range', icon: 'ri-calendar-event-line' },
                                ].map(tab => (
                                    <button
                                        key={tab.mode}
                                        onClick={() => setFilterMode(tab.mode)}
                                        style={{
                                            padding: '5px 16px',
                                            borderRadius: 20,
                                            border: filterMode === tab.mode ? 'none' : '1px solid #dee2e6',
                                            background: filterMode === tab.mode ? '#6259ca' : 'transparent',
                                            color: filterMode === tab.mode ? '#fff' : '#6c757d',
                                            fontWeight: filterMode === tab.mode ? 600 : 400,
                                            fontSize: 13,
                                            cursor: 'pointer',
                                            transition: 'all .2s',
                                        }}
                                    >
                                        <i className={`${tab.icon} me-1`} />{tab.label}
                                    </button>
                                ))}
                                <button
                                    onClick={fetchAnalysis}
                                    disabled={loading}
                                    style={{
                                        marginLeft: 'auto',
                                        padding: '5px 16px',
                                        borderRadius: 20,
                                        border: '1px solid #dee2e6',
                                        background: 'transparent',
                                        color: '#6259ca',
                                        fontWeight: 600,
                                        fontSize: 13,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <i className={`ri-refresh-line me-1 ${loading ? 'spin' : ''}`} />
                                    Refresh
                                </button>
                            </div>

                            {/* —— MODE 1: Quarter + Fiscal Year —— */}
                            {filterMode === MODE_QUARTER && (
                                <Row className="align-items-end g-3">
                                    <Col xs="auto">
                                        <p className="mb-1 fw-semibold text-muted" style={{ fontSize: 11 }}>QUARTER</p>
                                        <ButtonGroup size="sm">
                                            {QUARTERS.map(q => (
                                                <Button
                                                    key={q.value}
                                                    color="primary"
                                                    outline={selectedQuarter !== q.value}
                                                    onClick={() => setSelectedQuarter(q.value)}
                                                    style={{ minWidth: 88 }}
                                                >
                                                    {q.label}
                                                </Button>
                                            ))}
                                        </ButtonGroup>
                                    </Col>
                                    <Col xs="auto">
                                        <p className="mb-1 fw-semibold text-muted" style={{ fontSize: 11 }}>FISCAL YEAR</p>
                                        <ButtonGroup size="sm">
                                            <Button
                                                color="secondary"
                                                outline={selectedFY !== null}
                                                onClick={() => setSelectedFY(null)}
                                                style={{ minWidth: 72 }}
                                            >
                                                Auto
                                            </Button>
                                            {FISCAL_YEARS.map(fy => (
                                                <Button
                                                    key={fy.value}
                                                    color="secondary"
                                                    outline={selectedFY !== fy.value}
                                                    onClick={() => setSelectedFY(fy.value)}
                                                    style={{ minWidth: 80 }}
                                                >
                                                    {fy.label}
                                                </Button>
                                            ))}
                                        </ButtonGroup>
                                    </Col>
                                    {selectedFY && (
                                        <Col xs="auto">
                                            <div className="px-3 py-2 rounded" style={{ background: '#6259ca12', border: '1px solid #6259ca25', fontSize: 12, color: '#6259ca' }}>
                                                <i className="ri-information-line me-1" />
                                                {selectedQuarter === 'current' ? 'Same quarter' : selectedQuarter} of FY{selectedFY}-{String(selectedFY + 1).slice(2)}
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            )}

                            {/* —— MODE 2: Forecast Days —— */}
                            {filterMode === MODE_DAYS && (
                                <div>
                                    <p className="mb-2 fw-semibold text-muted" style={{ fontSize: 11 }}>NEXT N DAYS FROM TOMORROW</p>
                                    <ButtonGroup size="sm">
                                        {FORECAST_DAYS.map(d => (
                                            <Button
                                                key={d.value}
                                                color="secondary"
                                                outline={forecastDays !== d.value}
                                                onClick={() => setForecastDays(d.value)}
                                                style={{ minWidth: 54 }}
                                            >
                                                {d.label}
                                            </Button>
                                        ))}
                                    </ButtonGroup>
                                    {forecastDays && (
                                        <span className="ms-3 text-muted" style={{ fontSize: 12 }}>
                                            <i className="ri-calendar-check-line me-1" />
                                            Next {forecastDays} days starting tomorrow
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* —— MODE 3: Custom Date Range (Flatpickr) —— */}
                            {filterMode === MODE_RANGE && (
                                <div>
                                    <p className="mb-2 fw-semibold text-muted" style={{ fontSize: 11 }}>SELECT DATE RANGE</p>
                                    <Row className="align-items-end g-3">
                                        <Col xs="auto">
                                            <div style={{ position: 'relative' }}>
                                                <i className="ri-calendar-range-line"
                                                    style={{
                                                        position: 'absolute', left: 10, top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        color: '#6259ca', fontSize: 16, zIndex: 1, pointerEvents: 'none',
                                                    }}
                                                />
                                                <Flatpickr
                                                    value={startDate && endDate ? [startDate, endDate] : []}
                                                    options={{
                                                        mode: 'range',
                                                        dateFormat: 'Y-m-d',
                                                        maxDate: 'today',
                                                        showMonths: 2,
                                                        disableMobile: true,
                                                    }}
                                                    onChange={(dates) => {
                                                        if (dates.length === 2) {
                                                            setStartDate(fmtDate(dates[0]));
                                                            setEndDate(fmtDate(dates[1]));
                                                            setRangeApplied(false);
                                                            setRangeError('');
                                                        }
                                                    }}
                                                    placeholder="Select date range…"
                                                    style={{
                                                        paddingLeft: 34,
                                                        paddingRight: 12,
                                                        paddingTop: 8,
                                                        paddingBottom: 8,
                                                        border: '1px solid #dee2e6',
                                                        borderRadius: 8,
                                                        fontSize: 13,
                                                        color: '#495057',
                                                        width: 260,
                                                        cursor: 'pointer',
                                                        outline: 'none',
                                                        background: '#fff',
                                                    }}
                                                />
                                            </div>
                                        </Col>
                                        {/* <Col xs="auto">
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={applyRange}
                                                disabled={!startDate || !endDate || loading}
                                                style={{ minWidth: 110, borderRadius: 20, height: 36 }}
                                            >
                                                <i className="ri-search-line me-1" />
                                                {rangeApplied ? 'Refresh' : 'Apply Range'}
                                            </Button>
                                        </Col> */}
                                        {startDate && endDate && (
                                            <Col xs="auto">
                                                <div className="px-3 py-2 rounded d-flex align-items-center gap-1"
                                                    style={{ background: '#6259ca10', border: '1px solid #6259ca25', fontSize: 12 }}>
                                                    <i className="ri-calendar-check-line" style={{ color: '#6259ca' }} />
                                                    <span style={{ color: '#6259ca', fontWeight: 600 }}>{startDate}</span>
                                                    <span className="text-muted">→</span>
                                                    <span style={{ color: '#6259ca', fontWeight: 600 }}>{endDate}</span>
                                                </div>
                                            </Col>
                                        )}
                                        {rangeError && (
                                            <Col xs={12}>
                                                <span className="text-danger" style={{ fontSize: 12 }}>
                                                    <i className="ri-error-warning-line me-1" />{rangeError}
                                                </span>
                                            </Col>
                                        )}
                                        {rangeApplied && !loading && (
                                            <Col xs={12}>
                                                <span className="text-success" style={{ fontSize: 12 }}>
                                                    <i className="ri-check-line me-1" />Showing results for {startDate} → {endDate}
                                                </span>
                                            </Col>
                                        )}
                                    </Row>
                                </div>
                            )}


                        </CardBody>
                    </Card>

                    {/* ── Error ── */}
                    {error && (
                        <Alert color="danger" className="d-flex align-items-center gap-2">
                            <i className="ri-error-warning-line fs-5" />
                            <span>{error}</span>
                        </Alert>
                    )}

                    {/* ── Loading ── */}
                    {loading && (
                        <div className="d-flex flex-column align-items-center justify-content-center py-5">
                            <Spinner color="primary" style={{ width: 52, height: 52 }} />
                            <p className="text-muted mt-3">Fetching analysis data…</p>
                        </div>
                    )}

                    {/* ── No Data State ── */}
                    {!loading && result && !hasData && (
                        <Card className="shadow-sm mb-3">
                            <CardBody className="text-center py-5">
                                <i className="ri-inbox-line" style={{ fontSize: 64, opacity: 0.2, color: '#6259ca' }} />
                                <h5 className="mt-3 text-muted fw-semibold">No Data Available</h5>
                                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                                    There are no orders for the selected period. Try a different quarter, fiscal year, or date range.
                                </p>
                            </CardBody>
                        </Card>
                    )}

                    {/* ── Main Content ── */}
                    {!loading && result && hasData && (
                        <>
                            {/* ── Period Banner ── */}
                            <div
                                className="mb-3 rounded px-4 py-3 d-flex align-items-center gap-3 flex-wrap"
                                style={{
                                    background: 'linear-gradient(135deg, #6259ca15 0%, #f5a62315 100%)',
                                    border: '1px solid #6259ca25',
                                }}
                            >
                                <i className="ri-bar-chart-box-line fs-3 text-primary" />
                                <div className="flex-grow-1">
                                    <p className="mb-0 fw-semibold" style={{ color: '#3f3f60' }}>
                                        {result.period_label}
                                    </p>
                                    <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
                                        {result.period_start} &rarr; {result.period_end} &ensp;·&ensp; {apiMsg}
                                    </p>
                                </div>
                                {nextPeriod?.label && (
                                    <Badge
                                        className="fs-6 px-3 py-2"
                                        style={{ background: '#6259ca', fontWeight: 600 }}
                                    >
                                        <i className="ri-time-line me-1" />
                                        Next: {nextPeriod.label}
                                    </Badge>
                                )}
                            </div>

                            {/* ── KPI Summary Cards ── */}
                            <Row className="mb-3 g-3">
                                {[
                                    { label: 'Total Orders', value: totalOrders.toLocaleString(), icon: 'ri-shopping-bag-3-line', color: '#6259ca' },
                                    { label: 'Total Qty Sold', value: Math.round(totalQty).toLocaleString(), icon: 'ri-stack-line', color: '#02c0f8' },
                                    { label: 'Total Revenue', value: `₹${totalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, icon: 'ri-money-rupee-circle-line', color: '#f5a623' },
                                    { label: 'Peak Order Day', value: peakOrderDay, icon: 'ri-calendar-event-line', color: '#50cd89' },
                                    { label: 'Next Period Qty', value: nextPeriod?.total_qty_predicted?.range ?? `${(nextPeriod?.total_qty_predicted ?? 0)} units`, icon: 'ri-line-chart-line', color: '#ab47bc' },
                                ].map(kpi => (
                                    <Col key={kpi.label} xs={6} lg>
                                        <Card className="shadow-sm h-100 mb-0" style={{ borderTop: `3px solid ${kpi.color}` }}>
                                            <CardBody className="d-flex align-items-center gap-3 py-3">
                                                <div
                                                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                    style={{ width: 42, height: 42, background: `${kpi.color}18` }}
                                                >
                                                    <i className={`${kpi.icon} fs-5`} style={{ color: kpi.color }} />
                                                </div>
                                                <div>
                                                    <p className="mb-0 text-muted" style={{ fontSize: 10 }}>{kpi.label}</p>
                                                    <h5 className="mb-0 fw-bold" style={{ color: kpi.color }}>{kpi.value}</h5>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>

                            {/* ── Daily Trend Line Chart ── */}
                            <Card className="mb-3 shadow-sm">
                                <CardHeader className="border-bottom-0 pb-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
                                    <div>
                                        <h6 className="mb-0 fw-semibold">
                                            <i className="ri-line-chart-line me-2 text-primary" />
                                            Daily Orders &amp; Quantity Trend
                                        </h6>
                                        <p className="text-muted mb-0" style={{ fontSize: 11 }}>
                                            {dailyDates[0]} &rarr; {dailyDates[dailyDates.length - 1]}
                                            &ensp;·&ensp;{dailyDates.length} days
                                        </p>
                                    </div>
                                    <div className="d-flex gap-2 flex-wrap">
                                        <span className="badge px-3 py-2 d-flex align-items-center gap-1" style={{ background: '#6259ca18', color: '#6259ca', border: '1px solid #6259ca25', fontSize: 12 }}>
                                            <span style={{ width: 10, height: 3, background: '#6259ca', borderRadius: 2, display: 'inline-block' }} />
                                            Orders / Day
                                        </span>
                                        <span className="badge px-3 py-2 d-flex align-items-center gap-1" style={{ background: '#f5a62318', color: '#f5a623', border: '1px solid #f5a62325', fontSize: 12 }}>
                                            <span style={{ width: 10, height: 3, background: '#f5a623', borderRadius: 2, display: 'inline-block' }} />
                                            Qty / Day
                                        </span>
                                    </div>
                                </CardHeader>
                                <CardBody className="pt-2">
                                    <ReactApexChart
                                        options={dailyLineOptions}
                                        series={dailyLineSeries}
                                        type="line"
                                        height={340}
                                    />
                                </CardBody>
                            </Card>

                            {/* ── Top Products + Next Period Prediction ── */}
                            <Row className="mb-3 g-3">
                                {/* Top Products by Qty */}
                                <Col md={6}>
                                    <Card className="shadow-sm h-100">
                                        <CardHeader className="border-bottom-0 pb-2">
                                            <h6 className="mb-0 fw-semibold">
                                                <i className="ri-medal-line me-2 text-warning" />
                                                Top {topProducts.length} Most Ordered Products
                                            </h6>
                                            <p className="text-muted mb-0" style={{ fontSize: 11 }}>
                                                By total quantity ordered in selected period
                                            </p>
                                        </CardHeader>
                                        <CardBody className="pt-2 pb-3">
                                            {topProducts.length > 0 ? (() => {
                                                const maxQty = Math.max(...topProducts.map(p => p.total_qty_ordered ?? 0)) || 1;
                                                const PALETTE = ['#6259ca','#f5a623','#02c0f8','#50cd89','#f1416c','#7986cb','#4db6ac','#ff7043','#ab47bc','#26a69a'];
                                                return (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                        {topProducts.map((p, i) => {
                                                            const pct = Math.max(4, ((p.total_qty_ordered ?? 0) / maxQty) * 100);
                                                            const color = PALETTE[i % PALETTE.length];
                                                            return (
                                                                <div key={p.product_id}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                                        <span style={{
                                                                            minWidth: 22, height: 22, borderRadius: '50%',
                                                                            background: i < 3 ? '#f5a623' : `${color}20`,
                                                                            color: i < 3 ? '#fff' : color,
                                                                            fontSize: 10, fontWeight: 700,
                                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                            flexShrink: 0,
                                                                        }}>{p.rank}</span>
                                                                        <span style={{ fontSize: 12, color: '#3f3f60', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                                            title={p.product_name}>
                                                                            {p.product_name}
                                                                        </span>
                                                                        <span style={{
                                                                            fontSize: 11, fontWeight: 700, color: color,
                                                                            background: `${color}15`, border: `1px solid ${color}30`,
                                                                            borderRadius: 20, padding: '1px 8px', whiteSpace: 'nowrap', flexShrink: 0,
                                                                        }}>
                                                                            {(p.total_qty_ordered ?? 0).toLocaleString()} units
                                                                        </span>
                                                                    </div>
                                                                    <div style={{ height: 6, background: '#f0f0f8', borderRadius: 4, overflow: 'hidden' }}>
                                                                        <div style={{
                                                                            width: `${pct}%`, height: '100%',
                                                                            background: `linear-gradient(90deg, ${color}cc, ${color})`,
                                                                            borderRadius: 4,
                                                                            transition: 'width 0.6s ease',
                                                                        }} />
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })() : (
                                                <div className="d-flex align-items-center justify-content-center text-muted" style={{ height: 220 }}>
                                                    No product data.
                                                </div>
                                            )}
                                        </CardBody>
                                    </Card>
                                </Col>

                                {/* Next Period Predicted Qty */}
                                <Col md={6}>
                                    <Card className="shadow-sm h-100">
                                        <CardHeader className="border-bottom-0 pb-2">
                                            <h6 className="mb-0 fw-semibold">
                                                <i className="ri-line-chart-fill me-2 text-primary" />
                                                Next Period Demand Prediction
                                            </h6>
                                            <p className="text-muted mb-0" style={{ fontSize: 11 }}>
                                                {nextPeriod?.label ?? 'Upcoming period'} &ensp;·&ensp;
                                                {nextPeriod?.total_qty_predicted?.range ?? `${nextPeriod?.total_qty_predicted ?? 0} units`},&ensp;
                                                {nextPeriod?.total_orders_predicted?.range ?? `${nextPeriod?.total_orders_predicted ?? 0} orders`}
                                            </p>
                                        </CardHeader>
                                        <CardBody className="pt-2 pb-3">
                                            {(nextPeriod?.products ?? []).length > 0 ? (() => {
                                                const allProds = nextPeriod.products ?? [];
                                                const maxHigh = Math.max(...allProds.map(p => p.predicted_qty?.high ?? p.predicted_qty ?? 0)) || 1;
                                                const PALETTE = ['#50cd89','#6259ca','#02c0f8','#f5a623','#f1416c','#7986cb','#4db6ac','#ff7043','#ab47bc','#26a69a'];
                                                const RISK_COLORS = { high: '#f1416c', medium: '#f6a723', low: '#50cd89' };
                                                return (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                        {allProds.map((p, i) => {
                                                            const highVal = p.predicted_qty?.high ?? p.predicted_qty ?? 0;
                                                            const midVal  = p.predicted_qty?.mid  ?? 0;
                                                            const pct  = Math.max(4, (highVal / maxHigh) * 100);
                                                            const pctM = Math.max(0, (midVal  / maxHigh) * 100);
                                                            const color = PALETTE[i % PALETTE.length];
                                                            const riskColor = RISK_COLORS[p.shortage_risk] ?? RISK_COLORS.low;
                                                            const needsReorder = (p.recommended_reorder_qty ?? 0) > 0;
                                                            return (
                                                                <div key={p.product_id}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                                        <span style={{
                                                                            minWidth: 22, height: 22, borderRadius: '50%',
                                                                            background: needsReorder ? '#f1416c20' : `${color}20`,
                                                                            color: needsReorder ? '#f1416c' : color,
                                                                            fontSize: 10, fontWeight: 700,
                                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                            flexShrink: 0,
                                                                        }}>{p.rank}</span>
                                                                        <span style={{ fontSize: 12, color: '#3f3f60', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                                            title={p.product_name}>
                                                                            {p.product_name}
                                                                        </span>
                                                                        <span style={{
                                                                            fontSize: 11, fontWeight: 700, color: riskColor,
                                                                            background: `${riskColor}15`, border: `1px solid ${riskColor}30`,
                                                                            borderRadius: 20, padding: '1px 8px', whiteSpace: 'nowrap', flexShrink: 0,
                                                                        }}>
                                                                            {p.predicted_qty?.range ?? `${highVal} units`}
                                                                        </span>
                                                                        {needsReorder && (
                                                                            <span style={{
                                                                                fontSize: 10, fontWeight: 700, color: '#f1416c',
                                                                                background: '#f1416c15', border: '1px solid #f1416c30',
                                                                                borderRadius: 20, padding: '1px 7px', whiteSpace: 'nowrap', flexShrink: 0,
                                                                            }}>
                                                                                <i className="ri-shopping-cart-2-fill me-1" style={{ fontSize: 9 }} />
                                                                                {p.recommended_reorder_qty}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {/* Stacked bar: high (light) + mid (solid) */}
                                                                    <div style={{ height: 6, background: '#f0f0f8', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                                                                        <div style={{
                                                                            position: 'absolute', left: 0, top: 0,
                                                                            width: `${pct}%`, height: '100%',
                                                                            background: `${color}35`, borderRadius: 4,
                                                                        }} />
                                                                        <div style={{
                                                                            position: 'absolute', left: 0, top: 0,
                                                                            width: `${pctM}%`, height: '100%',
                                                                            background: `linear-gradient(90deg, ${color}cc, ${color})`,
                                                                            borderRadius: 4,
                                                                            transition: 'width 0.6s ease',
                                                                        }} />
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })() : (
                                                <div className="d-flex flex-column align-items-center justify-content-center text-muted" style={{ height: 220 }}>
                                                    <i className="ri-inbox-line fs-1" style={{ opacity: 0.25 }} />
                                                    <p className="mt-2 mb-0" style={{ fontSize: 13 }}>No predicted demand for next period.</p>
                                                </div>
                                            )}
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>

                            {/* ── Reorder Recommendations Alert ── */}
                            {reorderNeeded.length > 0 && (
                                <Card className="mb-3 shadow-sm" style={{ borderLeft: '4px solid #f1416c' }}>
                                    <CardHeader className="border-bottom-0 pb-0 d-flex align-items-center gap-2">
                                        <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{ width: 38, height: 38, background: '#f1416c15' }}>
                                            <i className="ri-alarm-warning-fill text-danger fs-5" />
                                        </div>
                                        <div>
                                            <h6 className="mb-0 fw-semibold text-danger">
                                                Reorder Required — {reorderNeeded.length} product{reorderNeeded.length > 1 ? 's' : ''}
                                            </h6>
                                            <p className="mb-0 text-muted" style={{ fontSize: 11 }}>
                                                Based on predicted next-period demand vs current stock
                                            </p>
                                        </div>
                                    </CardHeader>
                                    <CardBody className="pt-2">
                                        <Row className="g-2">
                                            {reorderNeeded.map(p => (
                                                <Col key={p.product_id} xs={12} md={6} lg={4}>
                                                    <div className="rounded p-3" style={{ background: '#f1416c08', border: '1px solid #f1416c25' }}>
                                                        <p className="mb-1 fw-semibold text-dark" style={{ fontSize: 13, lineHeight: 1.3 }}>
                                                            {p.product_name}
                                                        </p>
                                                        <p className="mb-2 text-muted" style={{ fontSize: 11 }}>SKU: {p.sku}</p>
                                                        <div className="d-flex gap-2 flex-wrap">
                                                            <span className="badge" style={{ background: '#f1416c18', color: '#f1416c', border: '1px solid #f1416c30', fontSize: 11 }}>
                                                                Stock: {p.current_stock ?? 0}
                                                            </span>
                                                            <span className="badge" style={{ background: '#f6a72318', color: '#f6a723', border: '1px solid #f6a72330', fontSize: 11 }}>
                                                                Reorder Lvl: {p.reorder_level ?? 0}
                                                            </span>
                                                            <span className="badge" style={{ background: '#6259ca18', color: '#6259ca', border: '1px solid #6259ca30', fontSize: 11 }}>
                                                                Reorder: {p.recommended_reorder_qty} units
                                                            </span>
                                                        </div>
                                                        <p className="mb-0 mt-2 text-muted" style={{ fontSize: 11, lineHeight: 1.4 }}>
                                                            {p.recommendation}
                                                        </p>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </CardBody>
                                </Card>
                            )}

                            {/* ── Top Products Table ── */}
                            <Card className="shadow-sm mb-3">
                                <CardHeader className="d-flex align-items-center justify-content-between gap-2">
                                    <div>
                                        <h6 className="mb-0 fw-semibold">
                                            <i className="ri-list-check-2 me-2 text-primary" />
                                            Top Products — Detailed Analysis
                                        </h6>
                                        <p className="mb-0 text-muted" style={{ fontSize: 11 }}>
                                            Ranked by orders in current period · Predicted qty for next period
                                        </p>
                                    </div>
                                </CardHeader>
                                <CardBody className="p-0">
                                    <div className="table-responsive">
                                        <Table className="table-hover align-middle mb-0" style={{ fontSize: 13 }}>
                                            <thead style={{ background: '#f8f9fc' }}>
                                                <tr>
                                                    <th className="ps-3 text-center" style={{ width: 40 }}>#</th>
                                                    <th>Product</th>
                                                    <th>SKU</th>
                                                    <th className="text-end">Total Orders</th>
                                                    <th className="text-end">Total Qty</th>
                                                    <th className="text-end">Avg Qty/Day</th>
                                                    <th className="text-end">Stock</th>
                                                    <th className="text-end">Reorder Lv.</th>
                                                    <th className="text-end">Next Predicted</th>
                                                    <th className="text-center">Risk</th>
                                                    <th className="text-end pe-3">Reorder Qty</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {topProducts.map(p => {
                                                    const rsk = RISK_CONFIG[p.shortage_risk] ?? RISK_CONFIG.low;
                                                    return (
                                                        <tr key={p.product_id}>
                                                            <td className="ps-3 text-center">
                                                                <Badge
                                                                    pill
                                                                    style={{
                                                                        background: p.rank <= 3 ? '#f5a623' : '#6259ca18',
                                                                        color: p.rank <= 3 ? '#fff' : '#6259ca',
                                                                        border: p.rank > 3 ? '1px solid #6259ca30' : 'none',
                                                                        fontWeight: 700,
                                                                    }}
                                                                >
                                                                    {p.rank}
                                                                </Badge>
                                                            </td>
                                                            <td className="fw-medium" style={{ maxWidth: 200 }}>
                                                                {p.product_name}
                                                            </td>
                                                            <td className="text-muted font-monospace">{p.sku}</td>
                                                            <td className="text-end fw-semibold">{p.total_orders ?? 0}</td>
                                                            <td className="text-end">{(p.total_qty_ordered ?? 0).toFixed(0)}</td>
                                                            <td className="text-end text-muted">{(p.avg_qty_per_day ?? 0).toFixed(2)}</td>
                                                            <td className="text-end fw-semibold">{(p.current_stock ?? 0).toLocaleString()}</td>
                                                            <td className="text-end text-muted">{p.reorder_level ?? 0}</td>
                                                            <td className="text-end">
                                                                <span className="fw-semibold" style={{ color: '#6259ca' }}>
                                                                    {`${p.predicted_qty_low ?? 0}–${p.predicted_qty_high ?? 0}`}
                                                                </span>
                                                            </td>
                                                            <td className="text-center">
                                                                <span
                                                                    className="badge rounded-pill d-inline-flex align-items-center gap-1"
                                                                    style={{
                                                                        background: `${rsk.color}18`,
                                                                        color: rsk.color,
                                                                        border: `1px solid ${rsk.color}40`,
                                                                        fontSize: 11,
                                                                        fontWeight: 600,
                                                                        padding: '3px 8px',
                                                                    }}
                                                                >
                                                                    <i className={rsk.icon} />
                                                                    {rsk.label}
                                                                </span>
                                                            </td>
                                                            <td className="text-end pe-3">
                                                                {(p.recommended_reorder_qty ?? 0) > 0 ? (
                                                                    <Badge color="danger" pill>{p.recommended_reorder_qty} units</Badge>
                                                                ) : (
                                                                    <span className="text-success fw-semibold">✓ OK</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {topProducts.length === 0 && (
                                                    <tr>
                                                        <td colSpan={11} className="text-center text-muted py-4">
                                                            No products to display.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </CardBody>
                            </Card>

                            {/* ── Next Period Full Recommendations ── */}
                            {nextPeriod && (
                                <Card className="shadow-sm mb-3">
                                    <CardHeader className="border-bottom-0">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                style={{ width: 38, height: 38, background: '#6259ca15' }}>
                                                <i className="ri-magic-line text-primary fs-5" />
                                            </div>
                                            <div>
                                                <h6 className="mb-0 fw-semibold">
                                                    Smart Reorder Recommendations — {nextPeriod.label}
                                                </h6>
                                                <p className="mb-0 text-muted" style={{ fontSize: 11 }}>
                                                    Predicted {nextPeriod.total_orders_predicted?.range ?? nextPeriod.total_orders_predicted},&ensp;
                                                    {nextPeriod.total_qty_predicted?.range ?? `${nextPeriod.total_qty_predicted} units`} needed
                                                </p>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardBody className="pt-0">
                                        <Row className="g-2">
                                            {(nextPeriod.products ?? []).map(p => {
                                                const rsk = RISK_CONFIG[p.shortage_risk] ?? RISK_CONFIG.low;
                                                const needsReorder = (p.recommended_reorder_qty ?? 0) > 0;
                                                return (
                                                    <Col key={p.product_id} xs={12} md={6} lg={4}>
                                                        <div
                                                            className="rounded p-3 h-100"
                                                            style={{
                                                                background: needsReorder ? '#f1416c06' : '#50cd8906',
                                                                border: `1px solid ${needsReorder ? '#f1416c30' : '#50cd8930'}`,
                                                            }}
                                                        >
                                                            <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                                                                <p className="mb-0 fw-semibold text-dark" style={{ fontSize: 13, lineHeight: 1.3 }}>
                                                                    {p.product_name}
                                                                </p>
                                                                <span
                                                                    className="badge rounded-pill flex-shrink-0"
                                                                    style={{
                                                                        background: `${rsk.color}18`,
                                                                        color: rsk.color,
                                                                        border: `1px solid ${rsk.color}40`,
                                                                        fontSize: 10,
                                                                    }}
                                                                >
                                                                    {rsk.label}
                                                                </span>
                                                            </div>
                                                            <div className="d-flex gap-2 flex-wrap mb-2">
                                                                <span style={{ fontSize: 11, color: '#6c757d' }}>
                                                                    <i className="ri-stack-line me-1" />
                                                                    Stock: <strong>{p.current_stock ?? 0}</strong>
                                                                </span>
                                                                <span style={{ fontSize: 11, color: '#6c757d' }}>
                                                                    <i className="ri-line-chart-line me-1" />
                                                                    Need: <strong style={{ color: '#6259ca' }}>{p.predicted_qty?.range ?? `${p.predicted_qty ?? 0} units`}</strong>
                                                                </span>
                                                            </div>
                                                            {needsReorder && (
                                                                <div className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded mb-2"
                                                                    style={{ background: '#f1416c15', border: '1px solid #f1416c30' }}>
                                                                    <i className="ri-shopping-cart-2-fill text-danger" style={{ fontSize: 12 }} />
                                                                    <span className="fw-bold text-danger" style={{ fontSize: 12 }}>
                                                                        Reorder: {p.recommended_reorder_qty} units
                                                                    </span>
                                                                </div>
                                                            )}
                                                            <p className="mb-0 text-muted" style={{ fontSize: 11, lineHeight: 1.4 }}>
                                                                {p.recommendation}
                                                            </p>
                                                        </div>
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                                    </CardBody>
                                </Card>
                            )}
                        </>
                    )}

                    {/* ── Empty State ── */}
                    {!loading && !result && !error && (
                        <div className="text-center py-5 text-muted">
                            <i className="ri-bar-chart-2-line" style={{ fontSize: 64, opacity: 0.2 }} />
                            <p className="mt-3">Select a quarter or date range to load analysis.</p>
                        </div>
                    )}
                </Container>
            </div>

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                .spin { display: inline-block; animation: spin 1s linear infinite; }
                .font-monospace { font-family: 'SFMono-Regular', Menlo, monospace; }
            `}</style>
        </React.Fragment>
    );
};

export default AnalysisPrediction;
